import React, { useState, useEffect } from 'react';
import { useAdminStore } from '../../../lib/store-admin';
import { Simulation } from '../../../lib/supabase';
import './simulation-editor.scss';
import avatarsData from '../../../data/avatars.json';

interface SimulationConfigEditorProps {
    institutionId: string;
    simulation?: Simulation;
    onClose: () => void;
}

const VOICE_OPTIONS = [
    { value: "Puck", label: "Puck" },
    { value: "Charon", label: "Charon" },
    { value: "Kore", label: "Kore" },
    { value: "Fenrir", label: "Fenrir" },
    { value: "Aoede", label: "Aoede" },
    { value: "Atari02", label: "Atari02" }
];

const DEFAULT_AVATARS = [
    { value: "/avatars/julia.glb", label: "Julia (Standard)" },
    { value: "/avatars/david.glb", label: "David (Sales)" }
];

const AVATAR_FILES = avatarsData.length > 0 ? avatarsData : DEFAULT_AVATARS;

export const SimulationConfigEditor: React.FC<SimulationConfigEditorProps> = ({ institutionId, simulation, onClose }) => {
    const { addSimulation, updateSimulation, isLoading } = useAdminStore();

    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [voice, setVoice] = useState('Puck');
    const [avatarUrl, setAvatarUrl] = useState('/avatars/julia.glb');

    const [systemInstruction, setSystemInstruction] = useState('');
    const [userInstruction, setUserInstruction] = useState('');
    const [missions, setMissions] = useState<string[]>([]);
    const [newMission, setNewMission] = useState('');
    const [configJson, setConfigJson] = useState('{}');
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (simulation) {
            setTitle(simulation.title);
            setDescription(simulation.description || '');

            // Config parsing
            try {
                const config = simulation.config || {};
                const instruction = config.systemInstruction?.parts?.[0]?.text || '';
                setSystemInstruction(instruction);
                setUserInstruction(config.userInstruction || '');
                setMissions(config.missions || []);

                const voiceName = config.voice || 'Puck';
                setVoice(voiceName);

                // Handle legacy avatarMode or new avatarUrl
                if (config.avatarUrl) {
                    setAvatarUrl(config.avatarUrl);
                } else if (config.avatarMode === 'door2door') {
                    setAvatarUrl('/avatars/david.glb');
                } else {
                    setAvatarUrl('/avatars/julia.glb');
                }

                // Other config parts as JSON
                const { systemInstruction: _, voice: __, avatarMode: ___, avatarUrl: ____, userInstruction: _____, missions: ______, ...restConfig } = config;
                setConfigJson(JSON.stringify(restConfig, null, 2));
            } catch (e) {
                console.error("Config parse error", e);
            }
        } else {
            setTitle('');
            setDescription('');
            setSystemInstruction('');
            setUserInstruction('');
            setMissions([]);
            setVoice('Puck');
            setAvatarUrl('/avatars/julia.glb');

            setConfigJson('{\n  "cards": [],\n  "products": []\n}');
        }
    }, [simulation]);

    const handleAddMission = () => {
        if (newMission.trim()) {
            setMissions([...missions, newMission.trim()]);
            setNewMission('');
        }
    };

    const handleRemoveMission = (index: number) => {
        setMissions(missions.filter((_, i) => i !== index));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);

        if (!title.trim()) {
            setError('Başlık zorunludur');
            return;
        }

        try {
            const parsedConfig = JSON.parse(configJson);

            // Construct final config object
            const finalConfig = {
                ...parsedConfig,
                systemInstruction: {
                    parts: [{ text: systemInstruction }]
                },
                voice: voice,
                avatarUrl: avatarUrl,
                userInstruction: userInstruction,
                missions: missions
            };

            let result;
            if (simulation) {
                result = await updateSimulation(simulation.id, {
                    title,
                    description,
                    config: finalConfig
                });
            } else {
                result = await addSimulation(institutionId, title, finalConfig);
            }

            if (result.success) {
                onClose();
            } else {
                setError(result.error || 'Kaydetme başarısız');
            }
        } catch (err: any) {
            setError('JSON format hatası: ' + err.message);
        }
    };

    return (
        <div className="simulation-config-editor">
            <div className="editor-header">
                <button className="btn-back" onClick={onClose}>
                    <span className="material-symbols-outlined">arrow_back</span>
                </button>
                <h2>{simulation ? 'Simülasyon Düzenle' : 'Yeni Simülasyon'}</h2>
                <div className="actions">
                    <button className="btn-cancel" onClick={onClose}>İptal</button>
                    <button className="btn-save" onClick={handleSubmit} disabled={isLoading}>
                        {isLoading ? 'Kaydediliyor...' : 'Kaydet'}
                    </button>
                </div>
            </div>

            {error && <div className="error-banner">{error}</div>}

            <div className="editor-content">
                <div className="form-section">
                    <h3>Genel Bilgiler</h3>
                    <div className="form-group">
                        <label>Simülasyon Başlığı</label>
                        <input
                            type="text"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            placeholder="Örn: İtiraz Karşılama Eğitimi"
                        />
                    </div>
                    <div className="form-group">
                        <label>Açıklama</label>
                        <textarea
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder="Kısa açıklama..."
                            rows={3}
                        />
                    </div>
                </div>

                <div className="form-section">
                    <h3>Görsel ve Ses Ayarları</h3>
                    <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                        <div className="form-group">
                            <label>Ses Seçimi</label>
                            <select value={voice} onChange={(e) => setVoice(e.target.value)}>
                                {VOICE_OPTIONS.map(opt => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                            </select>
                        </div>
                        <div className="form-group">
                            <label>Avatar Seçimi</label>
                            <select value={avatarUrl} onChange={(e) => setAvatarUrl(e.target.value)}>
                                {AVATAR_FILES.map(opt => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                            </select>
                        </div>

                    </div>
                </div>

                <div className="form-section">
                    <h3>Görevler (Simülasyon İçi Hedefler)</h3>
                    <div className="form-group">
                        <label>Senaryo sırasında tamamlanması gereken görevleri ekleyin</label>
                        <div className="mission-input-row" style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
                            <input
                                type="text"
                                value={newMission}
                                onChange={(e) => setNewMission(e.target.value)}
                                placeholder="Örn: Müşteriye internet hızından bahset"
                                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddMission())}
                            />
                            <button type="button" className="btn-add-mission" onClick={handleAddMission}>
                                <span className="material-symbols-outlined">add</span>
                            </button>
                        </div>
                        <div className="missions-list">
                            {missions.map((mission, index) => (
                                <div key={index} className="mission-item" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', background: 'rgba(255,255,255,0.05)', borderRadius: '6px', marginBottom: '4px' }}>
                                    <span>{mission}</span>
                                    <button type="button" className="btn-remove-mission" onClick={() => handleRemoveMission(index)} style={{ background: 'none', border: 'none', color: '#ff4d4d', cursor: 'pointer' }}>
                                        <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>delete</span>
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="form-section">
                    <h3>Konu / Persona Talimatları</h3>
                    <div className="form-group">
                        <label>AI'ın davranışını ve simülasyon konusunu belirleyin</label>
                        <textarea
                            value={systemInstruction}
                            onChange={(e) => setSystemInstruction(e.target.value)}
                            placeholder="AI'ın nasıl davranması gerektiğini buraya yazın..."
                            rows={10}
                        />
                    </div>
                </div>

                <div className="form-section">
                    <h3>Kullanıcı Yönergesi (Opsiyonel)</h3>
                    <div className="form-group">
                        <label>Simülasyon başlamadan önce kullanıcıya gösterilecek talimatlar</label>
                        <textarea
                            value={userInstruction}
                            onChange={(e) => setUserInstruction(e.target.value)}
                            placeholder="Kullanıcıya bu simülasyonda ne yapması gerektiğini anlatın..."
                            rows={5}
                        />
                    </div>
                </div>


            </div>
        </div>
    );
};

